<template>
	<router-link v-slot="{ isActive, navigate, href }" custom class="text-decoration-none" :to="navigateTo">
		<Row
			class="width-100 item-container"
			:class="{ selected: isActive, 'current-published': isCurrentPublishedVersion, 'current-draft': isCurrentDraftVersion }"
		>
			<div class="timeline height-100 relative">
				<div class="timeline-circle flex-centerize" :class="{ selected: isActive, 'current-published': isCurrentPublishedVersion }">
					<v-icon class="check-icon" v-if="isCurrentPublishedVersion">mdi-check</v-icon>
				</div>
			</div>
			<a
				:href="href"
				@click="(e) => navigate(e)"
				:class="{ selected: isActive }"
				class="text-decoration-none details-container width-100 px-3 display-flex flex-column"
			>
				<Row class="flex-align-center flex-justify-space-between mb-2">
					<Row class="flex-align-center">
						<WorkflowVersionIndication :versionNumber="workflowVersionNumber" />
						<h5 v-if="isCurrentPublishedVersion || isCurrentDraftVersion" class="ml-4 current-published-indication">
							Current {{ isCurrentDraftVersion ? 'draft' : 'published' }} version
						</h5>
					</Row>
					<Row class="flex-align-center">
						<h5 :class="{ light: !isPublished }" class="published-indication">{{ isPublished ? 'Published' : 'Not published' }}</h5>
						<WorkflowVersionItemDropdownMenu
							class="ml-2"
							:workflowId="workflowId"
							:workflowVersion="workflowVersion"
							:class="{ 'show-on-hover': !isActive }"
							:isCurrentDraftVersion="isCurrentDraftVersion"
							:allowDelete="!(isCurrentDraftVersion || isCurrentPublishedVersion)"
						/>
					</Row>
				</Row>
				<Row class="flex-align-center">
					<h5 class="description-text">{{ descriptionMessage }}</h5>
				</Row>
				<WorkflowVersionEditableDescription
					@onSave="onSave"
					:text="versionDescription"
					:isEditing.sync="isEditing"
					@select-version="(e) => navigate(e)"
					:hasEditPermissions="hasEditPermissions"
					:class="{ 'show-on-hover': !isEditing && !isActive }"
				/>
			</a>
		</Row>
	</router-link>
</template>
<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import debounce from 'lodash/debounce';
import { BUILDER_VERSION_HISTORY_ROUTE_NAME } from '@/router/router.const';
import { hasWorkflowEditPermissions } from '@/store/modules/workflows/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import AppAvatarService from '@/components/common/AppAvatar/app-avatar-service.js';
import { updateWorkflowVersion } from '@/store/modules/workflows/modules/versions/actions.js';
import WorkflowVersionService from '@/components/WorkflowVersionHistory/workflow-version-service';
import WorkflowVersionIndication from '@/components/WorkflowVersionHistory/WorkflowVersionIndication.vue';
import WorkflowVersionItemDropdownMenu from '@/components/WorkflowVersionHistory/WorkflowVersionItemDropdownMenu.vue';
import WorkflowVersionEditableDescription from '@/components/WorkflowVersionHistory/WorkflowVersionEditableDescription.vue';
export default {
	name: 'WorkflowVersionHistoryItem',
	components: {
		WorkflowVersionIndication,
		WorkflowVersionItemDropdownMenu,
		WorkflowVersionEditableDescription,
	},
	props: {
		workflow: { type: Object, required: true },
		workflowVersion: { type: Object, required: true },
		isCurrentDraftVersion: { type: Boolean, default: false },
		isCurrentPublishedVersion: { type: Boolean, default: false },
	},
	computed: {
		...mapGetters(['user']),
		...mapGettersShorthand({ hasWorkflowEditPermissions }),
		hasEditPermissions() {
			return this.hasWorkflowEditPermissions(this.workflowId, true);
		},
		workflowId() {
			return this.workflow.id;
		},
		isPublished() {
			return this.workflowVersion.is_published;
		},
		workflowVersionNumber() {
			return this.isCurrentDraftVersion ? this.draftVersionNumber : this.workflowVersion.version || 1;
		},
		draftVersionNumber() {
			return WorkflowVersionService.getWorkflowDraftVersionNumber(this.workflow);
		},
		publishedByUser() {
			return this.workflowVersion.published_by;
		},
		publishedTimeAgo() {
			return moment(this.workflowVersion.published_at).fromNow();
		},
		lastEditedTimeAgo() {
			return moment(this.workflowVersion.updated_at).fromNow();
		},
		currentVersionNumber() {
			return this.workflowVersion.version || 1;
		},
		publishedByUserFullName() {
			if (!this.isPublished || !this.publishedByUser) {
				return;
			}
			if (this.publishedByUser.id === this.user.id) {
				return 'you';
			}
			return AppAvatarService.getUserFullName(this.publishedByUser);
		},
		descriptionMessage() {
			if (this.isPublished) {
				return `Published by ${this.publishedByUserFullName} ${this.publishedTimeAgo}`;
			} else {
				return `Last edited ${this.lastEditedTimeAgo}`;
			}
		},
		navigateTo() {
			return { name: BUILDER_VERSION_HISTORY_ROUTE_NAME, params: { versionId: this.workflowVersion.id } };
		},
		versionDescription() {
			return this.workflowVersion.description;
		},
	},
	methods: {
		...mapActionsShorthand({ updateWorkflowVersion }),
		onSave: debounce(function (newText) {
			if (newText && newText !== this.versionDescription) {
				this.updateWorkflowVersion({
					workflowId: this.workflowId,
					workflowVersionId: this.workflowVersion.id,
					partialVersionObject: { description: newText },
				});
			}
		}, 500),
	},
	data() {
		return {
			isEditing: false,
		};
	},
};
</script>
<style scoped lang="scss">
@import '@/components/WorkflowVersionHistory/workflow-version-history-menu.scss';
.timeline {
	width: 2px;
	margin-right: 10px;
	background: rgba(223, 228, 239, 0.5);
}
.details-container {
	cursor: pointer;
	min-height: 60px;
	max-height: 90px;
	padding-top: 10px;
	border-radius: 10px;
	background: transparent;
	transition: var(--transition-time);

	&:hover {
		background: #f8faff;
		transition: var(--transition-time);
		.show-on-hover {
			display: flex;
		}
	}
	&:active {
		filter: brightness(0.9);
		transition: var(--transition-time);
	}
	&.selected {
		background: #f9fbff;
		border: 1px solid #dce0e9;
		transition: var(--transition-time);
	}
}

.published-indication {
	font-weight: 300;

	&.light {
		color: #929292;
	}
}
.item-container {
	min-height: 96px;
	background: transparent;

	opacity: 0.7;
	transition: var(--transition-time);
	&:hover,
	&:active,
	&.selected,
	&.current-draft,
	&.current-published {
		opacity: 1;
		transition: var(--transition-time);
	}
}
.timeline-circle {
	top: 17px;
	left: -4px;
	width: 10px;
	height: 10px;
	position: absolute;
	border-radius: 50%;
	background: #ffffff;
	border: 1px solid #a1a1a1;
	transition: var(--transition-time);

	&.selected {
		background: #a1a1a1;
		transition: var(--transition-time);
	}
	&.current-published {
		top: 14px;
		left: -6px;
		width: 14px;
		height: 14px;
		background: #5e5afa;
		transition: var(--transition-time);
	}
}
.relative {
	position: relative;
}
.check-icon {
	font-size: 12px;
	color: var(--white);
	font-weight: bold;
}
.current-published-indication {
	color: #5e5afa;
}
.show-on-hover {
	display: none;
}
</style>
